import {
  Component,
  effect,
  inject,
  Injector,
  OnInit,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { ColDef, GridOptions, IRowNode, RowClassParams } from '@ag-grid-community/core';
import { FieldTypes, AgGridComponent, MathCellEditorComponent } from '@digiwall/ag-grid';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { DwInputRequiredDirective, ErrorFormControlDirective } from '@digiwall/core';
import {
  CreateSupplierPreparationFromPurchaseOrderRequestDto,
  CreateSupplierPreparationFromPurchaseOrderResultDto,
  DayBookDto,
  DayBookService,
  ExistingSupplierPreparationDto,
  SupplierPurchaseOrderPreparationLineDto,
  SupplierPurchaseOrderService,
} from '@digiwall/goliath-documents/proxy';
import { DocRole } from '@digiwall/goliath-core/proxy';
import {
  GoliathDocumentsEnumerationTypeStore,
  SupplierPreparationStatus,
} from '../../../../enumeration-types';
import { PurchaseOrderLineToPrepareDto } from './models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrentCompanyStore } from '@digiwall/goliath-core';
import { ColumnDefinitionsService } from '../../../../services/column-definitions.service';
import { PreparationNavigationService } from '../../../../components';

@Component({
  selector: 'document-supplier-preparation-from-purchase-order',
  templateUrl: './supplier-preparation-from-purchase-order.component.html',
  styleUrl: './supplier-preparation-from-purchase-order.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    LocalizationModule,
    DialogBoxComponent,
    AgGridComponent,
    NgSelectModule,
    DwInputRequiredDirective,
    ErrorFormControlDirective,
  ],
})
export class SupplierPreparationFromPurchaseOrderComponent implements OnInit {
  constructor() {
    effect(() => {
      const grid = this.agGrid();
      const trigger = this.updateGridTrigger();

      if (!grid || trigger == 0) return;

      this.updateExtendedColumnVisibility();
      this.preselectRows();
    });
  }

  protected purchaseOrderId!: string;
  protected purchaseOrderConcurrencyStamp!: string;
  protected purchaseOrderReference?: string;
  protected supplierId?: string;
  protected supplierDeliveryModeId?: string;

  public setup(
    purchaseOrderId: string,
    purchaseOrderConcurrencyStamp: string,
    purchaseOrderReference: string = null,
    supplierId: string = null,
    supplierDeliveryModeId: string = null,
  ) {
    this.purchaseOrderId = purchaseOrderId;
    this.purchaseOrderConcurrencyStamp = purchaseOrderConcurrencyStamp;
    this.purchaseOrderReference = purchaseOrderReference;
    this.supplierId = supplierId;
    this.supplierDeliveryModeId = supplierDeliveryModeId;
  }

  private readonly injector = inject(Injector);
  private readonly offcanvasService = inject(NgbActiveOffcanvas);
  private readonly dayBookService = inject(DayBookService);
  private readonly purchaseOrderService = inject(SupplierPurchaseOrderService);
  private readonly localizationService = inject(LocalizationService);
  private readonly toasterService = inject(ToasterService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly preparationNavigation = inject(PreparationNavigationService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly currentCompanyStore = inject(CurrentCompanyStore);
  private readonly columnDefinitionsService = inject(ColumnDefinitionsService);

  protected readonly enumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);

  protected form: FormGroup;
  protected daybooks: DayBookDto[] = [];
  protected lines: PurchaseOrderLineToPrepareDto[] = [];
  protected selectedLines: PurchaseOrderLineToPrepareDto[] = [];
  protected existingPreparations: ExistingSupplierPreparationDto[] = [];
  protected selectedExistingPreparation: ExistingSupplierPreparationDto | null = null;
  protected readonly isSaving = signal(false);
  protected isLoading = true;

  protected preparationMode: 'new' | 'existing' = 'new';

  private readonly agGrid = viewChild<AgGridComponent>('agGrid');
  private readonly updateGridTrigger = signal<number>(0);

  protected dialogBoxTitle = '';
  protected readonly SupplierPreparationStatus = SupplierPreparationStatus;

  protected statusOptions: any[] = [];
  protected columnDefs: ColDef[] = [];
  protected readonly gridOptions: GridOptions = {
    stopEditingWhenCellsLoseFocus: true,
    rowClassRules: {
      'disabled-row': (params: RowClassParams<PurchaseOrderLineToPrepareDto>) =>
        !params.node.isSelected(),
    },
  };

  ngOnInit(): void {
    this.dialogBoxTitle = this.localizationService.instant(
      'Doc::SupplierPreparation:GenerateFromPurchaseOrder',
    );
    const allStatuses = this.enumerationTypeStore.supplierPreparationStatus();
    this.statusOptions = allStatuses.filter(
      s =>
        s.uniqueCode === SupplierPreparationStatus.Draft ||
        s.uniqueCode === SupplierPreparationStatus.Ready,
    );
    this.initForm();
    this.initColumnDefs();
    this.loadDaybooks();
    this.loadLines();
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      dayBookId: [null, Validators.required],
      statusId: [SupplierPreparationStatus.Draft, Validators.required],
      reference: [this.purchaseOrderReference, Validators.required],
      receivingInstruction: [null],
    });

    runInInjectionContext(this.injector, () => {
      this.form
        .get('dayBookId')!
        .valueChanges.pipe(takeUntilDestroyed())
        .subscribe(() => {
          this.onDayBookChange();
        });

      this.form
        .get('statusId')!
        .valueChanges.pipe(takeUntilDestroyed())
        .subscribe(statusId => {
          this.onStatusChange(statusId);
        });
    });
  }

  private initColumnDefs(): void {
    this.columnDefs = [
      {
        field: 'articleCode',
        headerName: this.localizationService.instant('Product::Article:Code'),
        type: FieldTypes.String,
        width: 120,
        editable: false,
        filter: false,
      },
      {
        colId: 'articleName',
        field: 'articleName.current',
        headerName: this.localizationService.instant('Product::Article:Name'),
        type: FieldTypes.String,
        flex: 1,
        editable: false,
        filter: false,
      },
      {
        field: 'backOrderQuantity',
        headerName: this.localizationService.instant('Doc::SupplierPreparation:BackOrderQuantity'),
        type: FieldTypes.Number,
        cellRendererParams: {
          decimals: this.currentCompanyStore.quantityDecimals,
        },
        cellEditor: MathCellEditorComponent,
        width: 130,
        editable: false,
        filter: false,
      },
      {
        field: 'waitingQuantity',
        headerName: this.localizationService.instant('Doc::SupplierPreparation:WaitingQuantity'),
        type: FieldTypes.Number,
        cellRendererParams: {
          decimals: this.currentCompanyStore.quantityDecimals,
        },
        cellEditor: MathCellEditorComponent,
        width: 130,
        editable: false,
        filter: false,
      },
      {
        field: 'inProgressQuantity',
        headerName: this.localizationService.instant('Doc::SupplierPreparation:InProgressQuantity'),
        type: FieldTypes.Number,
        cellRendererParams: {
          decimals: this.currentCompanyStore.quantityDecimals,
        },
        cellEditor: MathCellEditorComponent,
        width: 130,
        editable: false,
        filter: false,
      },
      {
        field: 'availableQuantity',
        headerName: this.localizationService.instant('Doc::SupplierPreparation:AvailableQuantity'),
        type: FieldTypes.Number,
        cellRendererParams: {
          decimals: this.currentCompanyStore.quantityDecimals,
        },
        cellEditor: MathCellEditorComponent,
        width: 130,
        editable: false,
        filter: false,
      },
      {
        field: 'quantityToPrepare',
        headerName: this.localizationService.instant('Doc::SupplierPreparation:QuantityToPrepare'),
        type: FieldTypes.Number,
        width: 150,
        filter: false,
        cellRendererParams: {
          decimals: this.currentCompanyStore.quantityDecimals,
        },
        cellEditor: MathCellEditorComponent,
        editable: params => params.node?.isSelected() ?? false,
        valueSetter: params => {
          const newValue = parseFloat(params.newValue);
          if (isNaN(newValue) || newValue <= 0) return false;

          const stockPieceSize: number | undefined = params.data.stockPieceSize;
          let finalValue = newValue;

          if (stockPieceSize) {
            const pieces = Math.ceil(newValue / stockPieceSize);
            finalValue = pieces * stockPieceSize;
            if (finalValue > params.data.availableQuantity) return false;
            if (finalValue !== newValue) {
              this.toasterService.warn(
                this.localizationService.instant(
                  'Doc::SupplierPreparation:QuantityRoundedToPieceSize',
                  finalValue.toString(),
                  stockPieceSize.toString(),
                ),
              );
            }
            params.data.quantityToPrepareStockUnit = pieces;
          } else {
            if (finalValue > params.data.availableQuantity) return false;
            params.data.quantityToPrepareStockUnit = finalValue;
          }

          params.data.quantityToPrepare = finalValue;
          params.api.refreshCells({
            rowNodes: [params.node!],
            columns: ['quantityToPrepareStockUnit'],
          });
          return true;
        },
      },
      {
        field: 'quantityToPrepareStockUnit',
        headerName: this.localizationService.instant(
          'Doc::SupplierPreparation:QuantityToPrepareStockUnit',
        ),
        type: FieldTypes.Number,
        cellRendererParams: {
          decimals: this.currentCompanyStore.quantityDecimals,
        },
        cellEditor: MathCellEditorComponent,
        width: 150,
        filter: false,
        editable: params => (params.node?.isSelected() ?? false) && !!params.data?.stockPieceSize,
        valueSetter: params => {
          const newValue = parseFloat(params.newValue);
          if (isNaN(newValue) || newValue <= 0) return false;

          const stockPieceSize: number = params.data.stockPieceSize;
          const purchaseQty = newValue * stockPieceSize;
          if (purchaseQty > params.data.availableQuantity) return false;

          params.data.quantityToPrepareStockUnit = newValue;
          params.data.quantityToPrepare = purchaseQty;
          params.api.refreshCells({
            rowNodes: [params.node!],
            columns: ['quantityToPrepare'],
          });
          return true;
        },
      },
      {
        field: 'articleLotName',
        headerName: this.localizationService.instant('Doc::StockDoc:ArticleLot'),
        type: FieldTypes.String,
        width: 120,
        hide: true,
        filter: false,
      },
      Object.assign({}, this.columnDefinitionsService.getArticleRange1ColDef(), {
        field: 'articleRange1Name',
        hide: true,
        filter: false,
      }),
      {
        field: 'articleRangeValue1Name',
        headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue1'),
        type: FieldTypes.String,
        width: 120,
        hide: true,
        filter: false,
      },
      Object.assign({}, this.columnDefinitionsService.getArticleRange2ColDef(), {
        field: 'articleRange2Name',
        hide: true,
        filter: false,
      }),
      {
        field: 'articleRangeValue2Name',
        headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue2'),
        type: FieldTypes.String,
        width: 120,
        hide: true,
        filter: false,
      },
      {
        field: 'length',
        headerName: this.localizationService.instant('Product::Stock:L'),
        type: FieldTypes.Number,
        width: 80,
        hide: true,
        editable: false,
        filter: false,
      },
      {
        field: 'width',
        headerName: this.localizationService.instant('Product::Stock:W'),
        type: FieldTypes.Number,
        width: 80,
        hide: true,
        editable: false,
        filter: false,
      },
      {
        field: 'thickness',
        headerName: this.localizationService.instant('Product::Stock:T'),
        type: FieldTypes.Number,
        width: 80,
        hide: true,
        editable: false,
        filter: false,
      },
    ];
  }

  private loadDaybooks(): void {
    this.dayBookService.getDayBooksByRole(DocRole.SupplierPreparation).subscribeOnce(daybooks => {
      this.daybooks = daybooks;
      if (daybooks.length > 0 && !this.form.get('dayBookId')!.value) {
        this.form.get('dayBookId')!.setValue(daybooks[0].id);
      }
    });
  }

  private loadLines(): void {
    this.isLoading = true;
    this.purchaseOrderService.getPreparationLines(this.purchaseOrderId).subscribeOnce(
      (preparationLines: SupplierPurchaseOrderPreparationLineDto[]) => {
        this.lines = preparationLines.map(
          line =>
            ({
              id: line.id!,
              purchaseOrderLineId: line.id!,
              articleCode: line.articleCode ?? '',
              articleName: line.articleName ?? '',
              backOrderQuantity: line.backOrderQuantity,
              waitingQuantity: line.pendingQuantity,
              inProgressQuantity: line.inPreparationQuantity,
              availableQuantity: line.availableQuantity,
              quantityToPrepare: line.quantityToPrepare,
              quantityToPrepareStockUnit: line.quantityToPrepareStockUnit,
              stockPieceSize: line.stockPieceSize,
              purchaseUnitCode: '',
              stockUnitCode: '',
              conversionFactor: 1,
              articleLotId: line.articleLotId,
              articleLotName: line.articleLotName,
              articleRange1Name: line.articleRange1Name,
              articleRangeValue1Id: line.articleRangeValue1Id,
              articleRangeValue1Name: line.articleRangeValue1Name,
              articleRange2Name: line.articleRange2Name,
              articleRangeValue2Id: line.articleRangeValue2Id,
              articleRangeValue2Name: line.articleRangeValue2Name,
              length: line.length,
              width: line.width,
              thickness: line.thickness,
              hasLot: !!line.articleLotId,
              hasRange1: !!line.articleRangeValue1Id,
              hasRange2: !!line.articleRangeValue2Id,
              hasDimension: !!(line.length || line.width || line.thickness),
            }) as PurchaseOrderLineToPrepareDto,
        );
        this.selectedLines = this.lines.filter(l => this.isRowSelectable(l));
        this.isLoading = false;
        this.updateGridTrigger.set(this.updateGridTrigger() + 1);
      },
      () => {
        this.isLoading = false;
      },
    );
  }

  private updateExtendedColumnVisibility(): void {
    const hasLot = this.lines.some(l => l.hasLot);
    const hasRange1 = this.lines.some(l => l.hasRange1);
    const hasRange2 = this.lines.some(l => l.hasRange2);
    const hasDimension = this.lines.some(l => l.hasDimension);

    this.columnDefs = this.columnDefs.map(col => {
      if (col.field === 'articleLotName') return { ...col, hide: !hasLot };
      if (col.field === 'articleRange1Name') return { ...col, hide: !hasRange1 };
      if (col.field === 'articleRangeValue1Name') return { ...col, hide: !hasRange1 };
      if (col.field === 'articleRange2Name') return { ...col, hide: !hasRange2 };
      if (col.field === 'articleRangeValue2Name') return { ...col, hide: !hasRange2 };
      if (col.field === 'length') return { ...col, hide: !hasDimension };
      if (col.field === 'width') return { ...col, hide: !hasDimension };
      if (col.field === 'thickness') return { ...col, hide: !hasDimension };
      return col;
    });
  }

  private preselectRows(): void {
    const selectedIds = new Set(this.selectedLines.map(l => l.id));
    this.agGrid()?.grid?.api?.forEachNode(node => {
      if (selectedIds.has(node.data?.id)) {
        node.setSelected(true);
      }
    });
  }

  protected onSelectionChanged(rows: PurchaseOrderLineToPrepareDto[]): void {
    this.selectedLines = rows;
    this.agGrid()?.grid?.api?.redrawRows();
  }

  protected isRowSelectable = (
    node: IRowNode<PurchaseOrderLineToPrepareDto> | PurchaseOrderLineToPrepareDto,
  ): boolean => {
    const data = 'data' in node ? node.data : node;
    return (data?.availableQuantity ?? 0) > 0;
  };

  private onStatusChange(statusId: string): void {
    if (statusId === SupplierPreparationStatus.Draft) {
      this.loadExistingPreparations();
    } else {
      this.existingPreparations = [];
      this.preparationMode = 'new';
      this.selectedExistingPreparation = null;
    }
  }

  private loadExistingPreparations(): void {
    const dayBookId = this.form.get('dayBookId')!.value;
    if (!dayBookId || !this.supplierId || !this.supplierDeliveryModeId) return;

    this.purchaseOrderService
      .getExistingPreparations(dayBookId, this.supplierId, this.supplierDeliveryModeId)
      .subscribeOnce(preparations => {
        this.existingPreparations = preparations;
      });
  }

  private onDayBookChange(): void {
    if (this.form.get('statusId')!.value === SupplierPreparationStatus.Draft) {
      this.loadExistingPreparations();
    }
  }

  protected onPreparationModeChange(mode: 'new' | 'existing'): void {
    this.preparationMode = mode;
    if (mode === 'new') {
      this.selectedExistingPreparation = null;
    }
  }

  protected onExistingPreparationChange(preparation: ExistingSupplierPreparationDto | null): void {
    this.selectedExistingPreparation = preparation;
    if (this.selectedExistingPreparation) {
      this.form.get('reference')!.setValue(this.selectedExistingPreparation.displayNumber);
      this.form
        .get('receivingInstruction')!
        .setValue(this.selectedExistingPreparation.receivingInstruction);
    }
  }

  protected canSave(): boolean {
    return (
      !this.isSaving() &&
      !this.isLoading &&
      this.selectedLines.length > 0 &&
      this.form.valid &&
      // Either create a new preparation, or (can only be 'existing') an existing preparation is selected
      (this.preparationMode == 'new' || this.selectedExistingPreparation != null)
    );
  }

  protected save(): void {
    if (!this.canSave()) return;

    const selectedLines = this.selectedLines;

    const request: CreateSupplierPreparationFromPurchaseOrderRequestDto = {
      purchaseOrderId: this.purchaseOrderId,
      purchaseOrderConcurrencyStamp: this.purchaseOrderConcurrencyStamp,
      dayBookId: this.form.get('dayBookId')!.value,
      statusId: this.form.get('statusId')!.value,
      reference: this.form.get('reference')!.value,
      receivingInstruction: this.form.get('receivingInstruction')!.value,
      existingPreparationId:
        this.preparationMode === 'existing' ? this.selectedExistingPreparation?.id : undefined,
      existingPreparationConcurrencyStamp:
        this.preparationMode === 'existing'
          ? this.selectedExistingPreparation?.concurrencyStamp
          : undefined,
      lines: selectedLines.map(line => ({
        purchaseOrderLineId: line.purchaseOrderLineId,
        quantityToPrepare: line.quantityToPrepare,
        articleLotId: line.articleLotId,
        articleRangeValue1Id: line.articleRangeValue1Id,
        articleRangeValue2Id: line.articleRangeValue2Id,
      })),
    };

    this.isSaving.set(true);
    this.purchaseOrderService.createSupplierPreparation(request).subscribeOnce(
      (result: CreateSupplierPreparationFromPurchaseOrderResultDto) => {
        this.isSaving.set(false);
        this.showResultSummary(result);
        this.offcanvasService.close(result);
      },
      () => {
        this.isSaving.set(false);
      },
    );
  }

  /**
   * Reports the generated preparation and offers to open it. This path always produces exactly one
   * document, so confirming goes straight there without the picker.
   */
  private showResultSummary(result: CreateSupplierPreparationFromPurchaseOrderResultDto): void {
    const summaryText = this.localizationService.instant(
      'Doc::SupplierPreparation:GenerationSuccess',
      result.displayNumber,
    );
    const generated = [{ id: result.preparationId, displayNumber: result.displayNumber }];
    const canOpen = this.preparationNavigation.hasGenerated(generated);

    this.confirmationService
      .info(
        { key: summaryText, defaultValue: summaryText },
        { key: '::Information', defaultValue: 'Information' },
        canOpen
          ? {
              yesText: 'Doc::SupplierPreparation:OpenGeneratedPreparation',
              cancelText: 'AbpAccount::Close',
            }
          : { hideCancelBtn: true, yesText: 'AbpAccount::Close' },
      )
      .subscribeOnce(status => {
        if (canOpen && status === Confirmation.Status.confirm) {
          this.preparationNavigation.openGenerated(generated, DocRole.SupplierPreparation);
        }
      });
  }
}
